import { useState } from 'react';
import { WebMercatorViewport } from '@deck.gl/core';
import { analysisApi, useFetchItemQuery } from '@/features/analysisApi';
import { selectItem, setListView } from '@/features/analysisMapSlice';
import { useAppDispatch } from '@/app/hooks';
import { getViewportFromBbox, LAYER_MANAGER_OFFSET_PX } from './deckglUtils';
import {
  useSmallScreen,
  useWindowDimensions,
} from '@/hooks/useWindowDimension';
import { SMALL_SCREEN_WIDTH } from '@/app/constants';
import type { WebMercatorViewportOptions } from './types';

/** This hook now handles calling the pole api endpoint when in the /point/:id url
 *
 * TODO: move this logic into the useUrlSync hook
 * https://linear.app/overstory/issue/WEBX-46/move-the-rest-of-useselectitem-into-useurlsync-hook
 */
export const useItemSelection = (
  url: string,
  currSelected: string | undefined,
  viewState: WebMercatorViewportOptions,
  isLayerManagerOpen: boolean = false
): {
  itemSelected: false | { longitude: number; latitude: number; zoom: number };
} => {
  const [prevSelection, setPrevSelection] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const { width, height } = useWindowDimensions();
  const isSmallScreen = useSmallScreen();

  const itemIsPole = url.startsWith('/point');
  const itemIsAddress = url.startsWith('/address');

  const shouldSelectItem = url !== '/' && url !== '' && !itemIsAddress;
  const shouldFetchPoleData =
    shouldSelectItem &&
    currSelected !== url &&
    prevSelection !== url &&
    itemIsPole;

  const { data: poleData } = useFetchItemQuery(
    { item: url },
    { skip: !shouldFetchPoleData }
  );

  if (!shouldSelectItem) {
    if (prevSelection !== null) {
      setPrevSelection(null);
    }
    return { itemSelected: false };
  }

  if (poleData && Object.keys(poleData).length !== 0 && shouldSelectItem) {
    dispatch(analysisApi.util.invalidateTags([{ type: 'poleData' }]));
    dispatch(
      selectItem({ object: poleData, layer: { id: 'poles' }, path: url })
    );
    if (isSmallScreen) {
      dispatch(setListView(false));
    }

    const currentViewport = new WebMercatorViewport(viewState);

    if (poleData.bbox) {
      const padding = determinePadding(
        width,
        height,
        isLayerManagerOpen,
        poleData.bbox,
        currentViewport.zoom
      );

      const viewport = getViewportFromBbox(viewState, poleData.bbox, padding);

      if (shouldFetchPoleData) {
        setPrevSelection(url);
      }

      if (viewport) {
        // Do not zoom user out if they are already zoomed in
        const zoom = Math.max(currentViewport.zoom, viewport.zoom);
        return { itemSelected: { ...viewport, zoom } };
      }
    }
  }

  return { itemSelected: false };
};

// This method calculate the left offset to apply based on zoom
const calculateZoomOffset = (zoom: number, widthToHeightRatio: number) => {
  let ZOOM_OFFSET = 355;
  if (widthToHeightRatio < 2 && widthToHeightRatio > 1.4) {
    if (zoom >= 17) {
      ZOOM_OFFSET = 200;
    }
    if (zoom >= 19) {
      ZOOM_OFFSET = 300;
    }
  }
  return ZOOM_OFFSET;
};
// Width to Height ratio to determine if we have a more horizontal or vertical span.
const WH_RATIO_BREAKPOINT = 7 / 10;

export const determinePadding = (
  width: number,
  height: number,
  isLayerManagerOpen: boolean,
  bbox: number[],
  zoom: number
) => {
  const [minLon, minLat, maxLon, maxLat] = bbox;
  const widthToHeightRatio = (maxLat - minLat) / (maxLon - minLon);
  // Determine the desired dimensions of the selected content
  const selectionWidthPx =
    1 / widthToHeightRatio >= 1 / WH_RATIO_BREAKPOINT ||
    (widthToHeightRatio >= WH_RATIO_BREAKPOINT &&
      1 / widthToHeightRatio >= WH_RATIO_BREAKPOINT)
      ? 300
      : 125;
  const selectionHeightPx = selectionWidthPx * widthToHeightRatio;

  // Calculate buffers based on window dimensions and desired dimensions of selected content.
  const leftOffset =
    width >= SMALL_SCREEN_WIDTH
      ? calculateZoomOffset(zoom, widthToHeightRatio)
      : 0;

  // Calculate additional offset based on zoom level to avoid span going out of bounds.
  const ADDITIONALOFFSET = zoom >= 17.5 ? 280 : 100;
  const rightOffset =
    width >= SMALL_SCREEN_WIDTH && isLayerManagerOpen
      ? LAYER_MANAGER_OFFSET_PX
      : width < SMALL_SCREEN_WIDTH
        ? 100
        : ADDITIONALOFFSET;
  const yBuffer = (height - selectionHeightPx) / 2;
  const xBuffer = (width - leftOffset - rightOffset - selectionWidthPx) / 2;
  return {
    top: yBuffer,
    bottom: yBuffer,
    left: xBuffer,
    right: xBuffer,
  };
};
